import React, { createContext, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Require props when the component has props other than onClose
type ActionSheetArgs<T> = keyof Omit<T, 'onClose'> extends never
  ? [React.ComponentType<T>]
  : [React.ComponentType<T>, Omit<T, 'onClose'> & { onClose?: () => void }];

export interface ActionSheetManagerContextType {
  showActionSheet: <T>(...args: ActionSheetArgs<T>) => void;
}

export const ActionSheetManagerContext =
  createContext<ActionSheetManagerContextType | null>(null);

export const useActionSheetManagerContext = () => {
  const context = useContext(ActionSheetManagerContext);
  if (!context) {
    throw new Error(
      'useActionSheetManagerContext requires an ActionSheetManagerProvider',
    );
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const ActionSheetManagerContextProvider: React.FC<Props> = ({
  children,
}) => {
  const [actionSheetQueue, setActionSheetQueue] = useState<JSX.Element[]>([]);

  const value: ActionSheetManagerContextType = useMemo(() => {
    return {
      showActionSheet: (...args) => {
        const [Component, props] = args;
        const { onClose, ...rest } = props || {};
        setActionSheetQueue((queue) => [
          ...queue,
          <Component
            key={uuidv4()}
            {...(rest as React.ComponentProps<typeof Component>)}
            onClose={() => {
              setActionSheetQueue((q) => q.slice(1));
              onClose?.();
            }}
          />,
        ]);
      },
    };
  }, []);

  return (
    <ActionSheetManagerContext.Provider value={value}>
      {children}
      {actionSheetQueue[0]}
    </ActionSheetManagerContext.Provider>
  );
};
